/**
 * IdentityContext — IDENTITY:P3.A React Context for Identity State
 *
 * Provides composite identity state via React Context API.
 * IdentityProvider accepts `user` prop (prop-drilled from App.jsx),
 * delegates to useIdentity hook.
 *
 * Solution Proposal: SOL-20260309-IDENTITY-P3
 */

import React, { createContext, useContext } from 'react';
import { useIdentity } from '../hooks/useIdentity';

export const IdentityContext = createContext(null);

/**
 * IdentityProvider — Wraps children with identity state from useIdentity.
 * @param {{ user: Object|null, children: React.ReactNode }} props
 */
export function IdentityProvider({ user, children }) {
  const { alias, aliasLower, avatarUrl, thumbnailUrl, fallbackLetter, fallbackColor, rankTier, rankScore, tier, totalPoints, isLoading, error, refetch } = useIdentity(user);

  const identity = {
    alias,
    aliasLower,
    avatarUrl,
    thumbnailUrl,
    fallbackLetter,
    fallbackColor,
    rankTier,
    rankScore,
    tier,
    totalPoints,
  };

  return (
    <IdentityContext.Provider value={{ identity, isLoading, error, refetch }}>
      {children}
    </IdentityContext.Provider>
  );
}

/**
 * useIdentityContext — Consume identity state from nearest IdentityProvider.
 * Throws if used outside of an IdentityProvider.
 * @returns {{ identity: Object, isLoading: boolean, error: string|null, refetch: Function }}
 */
export function useIdentityContext() {
  const ctx = useContext(IdentityContext);
  if (ctx === null) {
    throw new Error('useIdentityContext must be used within an IdentityProvider');
  }
  return ctx;
}
